import moment from "moment";
import React, { useState } from "react";
import { Modal, ModalBody, ModalFooter } from "reactstrap";

import { useModalSubmit } from "../../../common/useModalSubmit";

import {
  BooleanFeature,
  NumericFeature,
  isBooleanFeature,
  isNumericFeature,
} from "@pollyslack/hub/src/features/types";
import { getEnumValues } from "@pollyslack/hub/src/utils";

import { PlatformSubscriptionRow } from "../../../common/platformTypes";
import { DateTime } from "../../../components/Forms/DateTime";
import {
  BooleanInput,
  NumericInput,
} from "../../../components/UpdateSubscriptionModal";

/**
 * Platform's tier set. Only these four are self-serve — the rest of the enum
 * (TEAM, AUDIENCE, BUSINESS, CUSTOM, MANAGED_TRIAL_*) has no plan definition,
 * so nothing enforces a licence ceiling on them.
 */
const TIERS = ["FREE", "BASIC", "PRO", "UL_TEAM", "ENTERPRISE"];

export type PlatformSubscriptionUpdateParams = {
  tier: string;
  licenses: number;
  isTrial: boolean;
  isOneTime: boolean;
  endDate?: string;
  clearEndDate: boolean;
  stripeCustomerId?: string;
  stripeSubscriptionItemId?: string;
  billingAdminIds: string[];
  memo?: string;
  featureOverrides: Record<string, boolean | number>;
};

type Props = {
  isOpen: boolean;
  subscription: PlatformSubscriptionRow;
  onClose: () => void;
  onSubmit: (params: PlatformSubscriptionUpdateParams) => void | Promise<void>;
};

export const UpdatePlatformSubscriptionModal: React.FunctionComponent<
  Props
> = ({ isOpen, subscription, onClose, onSubmit }) => {
  const [tier, setTier] = useState(subscription.tier);
  const [licenses, setLicenses] = useState(subscription.licenses ?? 1);
  const [isTrial, setIsTrial] = useState(subscription.isTrial);
  const [isOneTime, setIsOneTime] = useState(!!subscription.isOneTime);
  const [endDate, setEndDate] = useState<Date | undefined>(
    subscription.endDate ? new Date(subscription.endDate) : undefined,
  );
  const [clearEndDate, setClearEndDate] = useState(false);
  const [stripeCustomerId, setStripeCustomerId] = useState(
    subscription.payment?.stripeCustomerId,
  );
  const [stripeSubscriptionItemId, setStripeSubscriptionItemId] = useState(
    subscription.payment?.stripeSubscriptionItemId,
  );
  const [billingAdminIds, setBillingAdminIds] = useState(
    subscription.billingAdminIds,
  );
  const [memo, setMemo] = useState(subscription.memo ?? "");
  const [featureOverrides, setFeatureOverrides] = useState<
    Record<string, boolean | number>
  >(subscription.featureOverrides ?? {});

  // Matches the server: `body("licenses").isInt({ min: 1 })` and
  // `assertSubscriptionShape` both refuse a subscription with no licence.
  const { submitting, error, submit } = useModalSubmit(isOpen);

  const licenseError =
    !Number.isInteger(licenses) || licenses < 1
      ? "Licenses must be a whole number, 1 or more"
      : "";

  const features = [
    ...getEnumValues(BooleanFeature),
    ...getEnumValues(NumericFeature),
  ].sort();

  function setBooleanOverride(feature: string, on: boolean) {
    const updated = { ...featureOverrides };
    // A boolean override can only grant. Platform stores `true` or nothing —
    // `false` is not representable and cannot revoke a tier's own grant.
    if (on) {
      updated[feature] = true;
    } else {
      delete updated[feature];
    }
    setFeatureOverrides(updated);
  }

  function setNumericOverride(feature: string, value: number) {
    setFeatureOverrides({ ...featureOverrides, [feature]: value });
  }

  return (
    <Modal isOpen={isOpen} style={{ maxWidth: "700px" }}>
      <div className="modal-header">
        <h5 className="modal-title">Update Platform Subscription</h5>
        <button
          type="button"
          className="close"
          aria-label="Close"
          onClick={onClose}
        >
          <span aria-hidden="true">&times;</span>
        </button>
      </div>
      <ModalBody>
        <div className="form-group">
          <label>Plan</label>
          <select
            className="form-control"
            value={tier}
            onChange={(e) => setTier(e.currentTarget.value)}
          >
            {/* A tier already on the row but off the self-serve list still
                has to be selectable, or opening the modal would change it. */}
            {(TIERS.includes(tier) ? TIERS : [tier, ...TIERS]).map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="platform-is-trial"
              checked={isTrial}
              onChange={(e) => setIsTrial(e.currentTarget.checked)}
            />
            <label htmlFor="platform-is-trial" className="form-check-label">
              Trial
            </label>
          </div>
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="platform-is-one-time"
              checked={isOneTime}
              onChange={(e) => setIsOneTime(e.currentTarget.checked)}
            />
            <label htmlFor="platform-is-one-time" className="form-check-label">
              One Time
            </label>
          </div>
        </div>

        <div className="form-group">
          <label>Number of Licenses</label>
          <input
            type="number"
            className={`form-control${licenseError ? " is-invalid" : ""}`}
            value={licenses}
            onChange={(e) => setLicenses(e.currentTarget.valueAsNumber)}
          />
          <span className="form-text text-danger">{licenseError}</span>
          {subscription.seatCount > licenses && (
            <span className="form-text text-warning">
              {subscription.seatCount} seats are in use — this leaves the team
              over quota.
            </span>
          )}
        </div>

        <div className="form-group">
          <label>
            Stripe Customer ID <i>(optional)</i>
          </label>
          <input
            type="text"
            className="form-control"
            placeholder="Stripe Customer ID..."
            defaultValue={subscription.payment?.stripeCustomerId}
            onChange={(e) => setStripeCustomerId(e.currentTarget.value)}
          />
          <span className="form-text text-muted">
            A customer on its own is not checked against Stripe — that is a plan
            billed manually against a Stripe customer. It is the subscription
            item below that gets verified, and this customer has to be the one
            that item belongs to.
          </span>
        </div>

        <div className="form-group">
          <label>
            Stripe Subscription Item ID <i>(optional, starts with si_)</i>
          </label>
          <input
            type="text"
            className="form-control"
            placeholder="Stripe Subscription Item ID..."
            defaultValue={subscription.payment?.stripeSubscriptionItemId}
            onChange={(e) => setStripeSubscriptionItemId(e.currentTarget.value)}
          />
          <span className="form-text text-muted">
            Leave blank for a subscription billed outside Stripe, or billed
            manually against a Stripe customer.
          </span>
        </div>

        <div className="form-group">
          <label>
            Billing Admin Ids <i>(comma separated)</i>
          </label>
          <input
            type="text"
            className="form-control"
            placeholder="Billing Admin Ids..."
            defaultValue={subscription.billingAdminIds.join(",")}
            onChange={(e) =>
              setBillingAdminIds(
                e.currentTarget.value
                  .split(",")
                  .map((id) => id.trim())
                  .filter(Boolean),
              )
            }
          />
        </div>

        <div className="form-group">
          <label>Memo</label>
          <input
            type="text"
            className="form-control"
            placeholder="why this change was made"
            defaultValue={subscription.memo}
            onChange={(e) => setMemo(e.currentTarget.value)}
          />
        </div>

        <div className="row">
          <div className="col">
            <label>
              Expiration Date <i>(optional)</i>
            </label>
          </div>
        </div>
        <div className="form-group row">
          <div className="col-9">
            <DateTime
              onChange={(e) => {
                setEndDate(moment(e).toDate());
                setClearEndDate(false);
              }}
              value={endDate}
            />
          </div>
          <div className="col-3">
            <button
              className="btn btn-outline-secondary w-100"
              onClick={() => {
                setEndDate(undefined);
                setClearEndDate(true);
              }}
            >
              Clear
            </button>
          </div>
        </div>

        <div className="form-group">
          <label>
            Feature Overrides <i>(optional)</i>
          </label>
          <div className="overflow-auto px-5" style={{ maxHeight: "30vh" }}>
            {features.map((f) => (
              <div className="form-row" key={f}>
                <div className="col">
                  <label htmlFor={`platform-${f}`}>{f}</label>
                </div>
                <div className="col-3 d-flex flex-row-reverse">
                  {isBooleanFeature(f) && (
                    <BooleanInput
                      id={`platform-${f}`}
                      defaultValue={featureOverrides[f] === true}
                      onChange={(v) => setBooleanOverride(f, v)}
                    />
                  )}
                  {isNumericFeature(f) && (
                    <NumericInput
                      defaultValue={Number(featureOverrides[f] ?? 0)}
                      minValue={0}
                      onChange={(v) => setNumericOverride(f, v)}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {error && <div className="alert alert-danger mb-0">{error}</div>}
      </ModalBody>
      <ModalFooter>
        <button
          className="btn btn-light"
          disabled={submitting}
          onClick={onClose}
        >
          Close
        </button>
        <button
          className="btn btn-primary"
          disabled={!!licenseError || submitting}
          onClick={() => {
            if (
              subscription.licenses !== undefined &&
              licenses < subscription.licenses &&
              !window.confirm(
                `Decrease licenses from ${subscription.licenses} to ${licenses}?`,
              )
            ) {
              return;
            }
            void submit(() =>
              onSubmit({
                tier,
                licenses,
                isTrial,
                isOneTime,
                endDate: endDate?.toISOString(),
                clearEndDate,
                stripeCustomerId,
                stripeSubscriptionItemId,
                billingAdminIds,
                // Sent as typed, empty included: an emptied box is the operator
                // rubbing the note out, and `undefined` would reach the server
                // as "not mentioned" and leave the stale note in place.
                memo,
                featureOverrides,
              }),
            );
          }}
        >
          {submitting ? "Saving…" : "Submit"}
        </button>
      </ModalFooter>
    </Modal>
  );
};
